import React from 'react';
import { Button, Table } from 'react-bootstrap';
import { getImageUrl } from '../utils/imageUtils';

function PaymentBadge({ paymentType, compact = false }) {
    return (
        <span
            style={{
                padding: compact ? '3px 8px' : '4px 8px',
                borderRadius: '4px',
                fontSize: compact ? '0.85em' : '0.9em',
                backgroundColor: paymentType === 'card' ? '#e3f2fd' : '#fff3e0',
                color: paymentType === 'card' ? '#1976d2' : '#e65100',
                display: 'inline-block',
            }}
        >
            {paymentType === 'card' ? (compact ? 'Карта' : '💳 Карта') : (compact ? 'Наличные' : '💵 Наличные')}
        </span>
    );
}

const SalesHistoryGrouped = ({
    orderGroups,
    expandedOrders,
    onToggleOrder,
    isMainAdmin,
    onReturnSale,
    onImageClick,
}) => {
    const isExpanded = (group) => group.itemCount === 1 || !!expandedOrders[group.orderId];

    const itemsLabel = (count) => {
        if (count === 1) return '1 товар';
        if (count < 5) return `${count} товара`;
        return `${count} товаров`;
    };

    const renderItemFields = (sale) => (
        <>
            {sale.image_url && (
                <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                    <img
                        src={getImageUrl(sale.image_url)}
                        alt={sale.product}
                        style={{
                            width: 80,
                            height: 80,
                            objectFit: 'cover',
                            borderRadius: 8,
                            cursor: 'pointer',
                            border: '1px solid #ddd',
                        }}
                        onClick={() => onImageClick(getImageUrl(sale.image_url))}
                        loading="lazy"
                    />
                </div>
            )}
            <div>
                <div className="sales-label"><span className="sales-icon">📦</span>Товар:</div>
                <span className="sales-value">{sale.product}</span>
            </div>
            <div>
                <div className="sales-label"><span className="sales-icon">🔖</span>Артикул:</div>
                <span className="sales-value">{sale.article || '-'}</span>
            </div>
            <div>
                <div className="sales-label"><span className="sales-icon">📏</span>Размер:</div>
                <span className="sales-value">{sale.variantName || sale.size || '-'}</span>
            </div>
            <hr className="sales-divider" />
            <div>
                <div className="sales-label"><span className="sales-icon">💸</span>Цена:</div>
                <span className="sales-value">{sale.price} ₽</span>
            </div>
            {sale.isReturned && (
                <span className="sales-status-badge mt-2">Позиция возвращена</span>
            )}
            {isMainAdmin && !sale.isReturned && (
                <button
                    type="button"
                    className="sales-delete-btn w-100"
                    onClick={() => onReturnSale(sale)}
                >
                    🗑 Удалить (возврат)
                </button>
            )}
        </>
    );

    return (
        <>
            <div className="sales-order-groups d-none d-md-flex">
                {orderGroups.map((group) => {
                    const expanded = isExpanded(group);
                    const canToggle = group.itemCount > 1;
                    return (
                        <div
                            key={group.orderId}
                            className={`sales-order-group${group.allReturned ? ' returned' : ''}`}
                        >
                            <div
                                className={`sales-order-header${canToggle ? ' clickable' : ''}`}
                                onClick={canToggle ? () => onToggleOrder(group.orderId) : undefined}
                                onKeyDown={canToggle ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onToggleOrder(group.orderId);
                                    }
                                } : undefined}
                                role={canToggle ? 'button' : undefined}
                                tabIndex={canToggle ? 0 : undefined}
                            >
                                <div className="sales-order-header-main">
                                    <span className="sales-icon">🧾</span>
                                    <span className="sales-order-num">№ {group.orderNum}</span>
                                    {group.allReturned ? (
                                        <span className="sales-status-badge">Возвращено</span>
                                    ) : (
                                        <span className="sales-status-badge sold">Продано</span>
                                    )}
                                    {canToggle && (
                                        <span className="sales-order-toggle" aria-hidden="true">
                                            {expanded ? '▼' : '▶'}
                                        </span>
                                    )}
                                    {canToggle && (
                                        <span className="sales-order-items-count">{itemsLabel(group.itemCount)}</span>
                                    )}
                                </div>
                                <div className="sales-order-header-meta">
                                    <span className="sales-order-total">
                                        Сумма заказа: <strong>{group.orderTotal.toFixed(2)} ₽</strong>
                                    </span>
                                    <PaymentBadge paymentType={group.paymentType} />
                                    <span className="sales-order-date">{group.dateTime}</span>
                                </div>
                            </div>
                            {expanded && (
                                <Table bordered size="sm" className="sales-order-items-table mb-0">
                                    <thead>
                                        <tr>
                                            <th>Фото</th>
                                            <th>Товар</th>
                                            <th>Артикул</th>
                                            <th>Размер</th>
                                            <th>Цена</th>
                                            {isMainAdmin && <th>Действия</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {group.items.map((sale) => (
                                            <tr key={sale.id} className={sale.isReturned ? 'table-success' : ''}>
                                                <td>
                                                    {sale.image_url ? (
                                                        <img
                                                            src={getImageUrl(sale.image_url)}
                                                            alt={sale.product}
                                                            style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, cursor: 'pointer' }}
                                                            onClick={() => onImageClick(getImageUrl(sale.image_url))}
                                                            loading="lazy"
                                                        />
                                                    ) : (
                                                        <span style={{ color: '#999' }}>—</span>
                                                    )}
                                                </td>
                                                <td>{sale.product}</td>
                                                <td>{sale.article || '-'}</td>
                                                <td>{sale.variantName || sale.size || '-'}</td>
                                                <td>{sale.price} ₽</td>
                                                {isMainAdmin && (
                                                    <td>
                                                        {!sale.isReturned ? (
                                                            <Button variant="danger" size="sm" onClick={() => onReturnSale(sale)}>
                                                                Удалить
                                                            </Button>
                                                        ) : (
                                                            <span className="text-success">Возвращено</span>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="sales-table-mobile d-md-none">
                {orderGroups.map((group) => {
                    const expanded = isExpanded(group);
                    const canToggle = group.itemCount > 1;
                    return (
                        <div
                            key={group.orderId}
                            className={`sales-order-group-mobile${group.allReturned ? ' returned' : ''}`}
                        >
                            <div
                                className={`sales-order-header-mobile${canToggle ? ' clickable' : ''}`}
                                onClick={canToggle ? () => onToggleOrder(group.orderId) : undefined}
                            >
                                <div className="sales-header">
                                    <span className="sales-icon" style={{ fontSize: '1.4em' }}>🧾</span>
                                    <span>№ {group.orderNum}</span>
                                    {group.allReturned ? (
                                        <span className="sales-status-badge">Возвращено</span>
                                    ) : (
                                        <span className="sales-status-badge" style={{ background: 'linear-gradient(90deg,#2563eb 0%,#38bdf8 100%)', color: '#fff' }}>Продано</span>
                                    )}
                                    {canToggle && (
                                        <span className="sales-order-toggle-mobile">{expanded ? '▼' : '▶'}</span>
                                    )}
                                </div>
                                <div className="sales-order-summary-mobile">
                                    <div>
                                        <span className="sales-label">Сумма заказа:</span>
                                        <span className="sales-value">{group.orderTotal.toFixed(2)} ₽</span>
                                    </div>
                                    <div>
                                        <span className="sales-label">Оплата:</span>
                                        <PaymentBadge paymentType={group.paymentType} compact />
                                    </div>
                                    <div>
                                        <span className="sales-label">Дата:</span>
                                        <span className="sales-value">{group.dateTime}</span>
                                    </div>
                                    {canToggle && (
                                        <div className="sales-order-items-hint">
                                            {itemsLabel(group.itemCount)} — нажмите, чтобы {expanded ? 'свернуть' : 'раскрыть'}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {expanded && group.items.map((sale) => (
                                <div key={sale.id} className={`sales-row sales-row-item${sale.isReturned ? ' returned' : ''}`}>
                                    {renderItemFields(sale)}
                                </div>
                            ))}
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default SalesHistoryGrouped;
